/**
 * AD/Entra Sync — per-run proposals review page (admin).
 *
 * Shows the full diff for one ad_sync_run: filterable, batch-applyable.
 * Manual review proposals can be rejected with a note. Other action
 * types can be applied individually or in bulk.
 *
 * There is no separate "approve" step: Apply IS the approval, and it writes
 * the change at once (creates the sales position, sets reports-to, assigns
 * the person, links the CRM account to AD). "Select all" (whole run, or one
 * group) only selects; nothing happens until "Apply selected" is confirmed.
 * Manual-review items are never bulk-selectable: each needs its own decision.
 * Bulk apply goes through apply_ad_sync_actions_batch in chunks of 100,
 * creates first, so every item gets the same server checks as a single
 * Apply. Bulk apply surfaces
 * WAITING_ON_DEPENDENCY per the auditor's UI note: when batch hits it,
 * we render a clear "apply the dependent create first, then retry"
 * banner and a Retry button.
 */

import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import {
  useAdSyncProposals,
  useAdSyncRun,
  useAdSyncRuns,
  useApplyAdSyncAction,
  useApplyAdSyncActionsInChunks,
  useComputeAdSyncProposals,
  useRejectAdSyncAction,
} from "../hooks/useAdSync";
import { useToast } from "../components/ui/Toast";
import Button from "../components/common/Button";
import Card from "../components/ui/Card";
import LoadingSpinner from "../components/common/LoadingSpinner";
import {
  adSyncSourceLabel,
  isWaitingOnDependencyError,
  latestComputedRunId,
  type ChunkedApplyResult,
} from "../services/adSync.service";

/**
 * Supabase RPC errors are plain objects (not Error instances), so `e instanceof Error` dropped the server's sentence.
 * Server refusals that start with a stable code (e.g. MSR_REP_HAS_OPEN_DAY from the sales-position guard, migration
 * 504: the person has a started, unclosed event day) are shown without the code.
 */
function readableApplyError(e: unknown, fallback: string): string {
  const raw =
    typeof e === "object" && e !== null && "message" in e
      ? String((e as { message: unknown }).message ?? "")
      : typeof e === "string"
        ? e
        : "";
  if (!raw) {
    return fallback;
  }
  return raw.replace(/^(MSR|TERRITORY|FIELD_MANAGER)_[A-Z_]+:\s*/, "");
}
import type {
  AdSyncAction,
  AdSyncActionStatus,
  AdSyncActionType,
  AdSyncProposalFilters,
} from "../types/adSync.types";

/** Run-wide list of open proposals: what "Select all" counts and selects. */
const RUN_PROPOSED_FILTER: AdSyncProposalFilters = { status: "proposed" };

function isBulkSelectable(a: AdSyncAction): boolean {
  return a.status === "proposed" && a.action_type !== "manual_review_required";
}

function actionDisplayName(a: AdSyncAction | undefined): string {
  if (!a) {
    return "";
  }
  const src = (a.source ?? {}) as Record<string, unknown>;
  return (
    str(src.graph_display_name) ??
    str(src.graph_upn) ??
    a.azure_ad_user_id ??
    ""
  );
}

const STATUS_LABELS: Record<AdSyncActionStatus, string> = {
  proposed: "Proposed",
  applied: "Applied",
  rejected: "Rejected",
  superseded: "Superseded",
};

const STATUS_STYLES: Record<AdSyncActionStatus, string> = {
  proposed: "bg-blue-100 text-blue-800",
  applied: "bg-green-100 text-green-800",
  rejected: "bg-gray-200 text-gray-700",
  superseded: "bg-yellow-100 text-yellow-800",
};

const ACTION_LABELS: Record<string, string> = {
  create_sales_position: "Create position",
  update_sales_position_person: "Assign person",
  update_sales_position_parent: "Set reports-to",
  link_profile_azure_ad_user_id: "Link CRM account to AD",
  update_sales_position_attribute: "Update attribute",
  vacate_sales_position: "Vacate assignment",
  manual_review_required: "Manual review",
};

const ACTION_ORDER: AdSyncActionType[] = [
  "create_sales_position",
  "link_profile_azure_ad_user_id",
  "update_sales_position_person",
  "update_sales_position_parent",
  "vacate_sales_position",
  "update_sales_position_attribute",
  "manual_review_required",
];

function StatusPill({ status }: { status: AdSyncActionStatus }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

function FlagPills({ flags }: { flags: string[] }) {
  if (!flags?.length) {
    return null;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {flags.map((f) => (
        <span
          key={f}
          className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 text-xs border border-amber-200"
        >
          {f}
        </span>
      ))}
    </div>
  );
}

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

/**
 * One readable line per proposal (mig 600): who the person will report to and
 * who they report to now, how the CRM account was matched, or which unmapped
 * title a review card stands for. The raw JSON stays under "Show diff".
 */
/** Mig 1243: what applying did to the CRM role (manager -> RM / TM), or why it was left for review. */
function RoleOutcome({ action }: { action: AdSyncAction }) {
  const applied = (action.applied_changes ?? {}) as Record<string, unknown>;
  const moved = applied.role_moved as Record<string, unknown> | undefined;
  const review = applied.role_review as Record<string, unknown> | undefined;
  const src = (action.source ?? {}) as Record<string, unknown>;
  return (
    <>
      {src.title_matched_by === "title_rule" && (
        <div className="text-xs text-gray-600 mt-1" data-testid="ad-title-rule">
          Position type from the job title rule (title contains &ldquo;
          {String(src.crm_position_type ?? "").replace("_", " ")}&rdquo;)
        </div>
      )}
      {moved && (
        <div
          className="text-sm text-green-800 mt-1"
          data-testid="ad-role-moved"
        >
          CRM role changed from Manager to{" "}
          {String(moved.to ?? "").replace("_", " ")}
        </div>
      )}
      {review && (
        <div
          className="text-sm text-amber-800 mt-1"
          data-testid="ad-role-review"
        >
          Role not changed ({String(review.role ?? "?")}): review it on the
          Users page
        </div>
      )}
      {applied.msr_invite_queued === true && (
        <div
          className="text-sm text-gray-700 mt-1"
          data-testid="ad-invite-queued"
        >
          MSR invitation queued (sent automatically)
        </div>
      )}
    </>
  );
}

function ProposalSummary({ action }: { action: AdSyncAction }) {
  const src = (action.source ?? {}) as Record<string, unknown>;
  const after = (action.after_jsonb ?? {}) as Record<string, unknown>;
  if (action.action_type === "update_sales_position_parent") {
    const parentName = str(src.parent_graph_display_name) ?? "(unknown)";
    const parentTitle = str(src.parent_graph_job_title);
    const current =
      str(src.current_parent_holder) ?? str(src.current_parent_title);
    return (
      <div className="text-sm text-gray-800 mt-1" data-testid="ad-parent-line">
        Reports to <strong>{parentName}</strong>
        {parentTitle ? ` (${parentTitle})` : ""}
        <span className="text-gray-500">
          {" "}
          · currently: {current ?? "no manager"}
        </span>
        {after.parent_position_id ? null : (
          <span className="text-gray-500">
            {" "}
            · manager's position is created in this run
          </span>
        )}
      </div>
    );
  }
  if (action.action_type === "link_profile_azure_ad_user_id") {
    return (
      <div className="text-sm text-gray-800 mt-1">
        CRM account matched by{" "}
        <strong>{str(src.matched_via) ?? "identity"}</strong>
        {str(src.matched_profile_role)
          ? ` · CRM role: ${str(src.matched_profile_role)}`
          : ""}
      </div>
    );
  }
  if (
    action.action_type === "manual_review_required" &&
    after.reason === "unmapped_title"
  ) {
    return (
      <div className="text-sm text-gray-800 mt-1">
        Unmapped job title{" "}
        <strong>&ldquo;{str(after.observed_title) ?? "(blank)"}&rdquo;</strong>{" "}
        · {String(after.head_count ?? "?")}{" "}
        {after.head_count === 1 ? "person" : "people"}
      </div>
    );
  }
  return null;
}

function JsonBlock({
  label,
  value,
}: {
  label: string;
  value: Record<string, unknown> | null | undefined;
}) {
  if (!value || Object.keys(value).length === 0) {
    return (
      <div>
        <div className="text-xs uppercase text-gray-500 mb-1">{label}</div>
        <div className="text-xs text-gray-400 italic">empty</div>
      </div>
    );
  }
  return (
    <div>
      <div className="text-xs uppercase text-gray-500 mb-1">{label}</div>
      <pre className="bg-gray-50 border border-gray-200 rounded p-2 text-xs overflow-x-auto whitespace-pre-wrap">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

interface ProposalCardProps {
  action: AdSyncAction;
  selected: boolean;
  onSelectToggle: (id: string, checked: boolean) => void;
  onApply: (action: AdSyncAction) => void;
  onReject: (action: AdSyncAction) => void;
  busy: boolean;
}

function ProposalCard({
  action,
  selected,
  onSelectToggle,
  onApply,
  onReject,
  busy,
}: ProposalCardProps) {
  const isManualReview = action.action_type === "manual_review_required";
  const isProposed = action.status === "proposed";
  const src = (action.source ?? {}) as Record<string, unknown>;
  return (
    <div
      className={`border rounded-lg p-4 ${
        selected ? "border-red-400 bg-red-50/30" : "border-gray-200 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {isProposed && !isManualReview && (
            <input
              type="checkbox"
              checked={selected}
              onChange={(e) => onSelectToggle(action.id, e.target.checked)}
              className="mt-1 h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
            />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm text-gray-900">
                {ACTION_LABELS[action.action_type] ?? action.action_type}
              </span>
              <StatusPill status={action.status} />
            </div>
            <ProposalSummary action={action} />
            <RoleOutcome action={action} />
            <div className="text-sm text-gray-700 mt-1">
              {(src.graph_display_name as string) ??
                (action.after_jsonb?.reason === "unmapped_title"
                  ? "Job title review"
                  : "(no name)")}
              {src.graph_job_title ? (
                <span className="text-gray-500">
                  {" "}
                  · {src.graph_job_title as string}
                </span>
              ) : null}
            </div>
            <div className="text-xs text-gray-500 mt-0.5 truncate">
              {(src.graph_upn as string) ?? action.azure_ad_user_id ?? "—"}
            </div>
            <div className="mt-2">
              <FlagPills flags={action.flags ?? []} />
            </div>
          </div>
        </div>
        {isProposed && (
          <div className="flex flex-col gap-2 shrink-0">
            {!isManualReview && (
              <Button
                variant="primary"
                onClick={() => onApply(action)}
                disabled={busy}
              >
                Apply
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => onReject(action)}
              disabled={busy}
            >
              Reject
            </Button>
          </div>
        )}
      </div>

      {action.reason && (
        <div className="mt-3 text-sm text-gray-700">
          <span className="text-gray-500 text-xs uppercase">Reason:</span>{" "}
          {action.reason}
        </div>
      )}

      <details className="mt-3">
        <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-900">
          Show diff / raw payload
        </summary>
        <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
          <JsonBlock label="Before" value={action.before_jsonb} />
          <JsonBlock label="After (proposed)" value={action.after_jsonb} />
          {action.applied_changes &&
            Object.keys(action.applied_changes).length > 0 && (
              <JsonBlock
                label="Applied changes"
                value={action.applied_changes}
              />
            )}
          <JsonBlock label="Source identity" value={action.source} />
        </div>
      </details>

      {action.status !== "proposed" && (
        <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
          {action.status === "applied" &&
            action.applied_at &&
            `Applied ${format(new Date(action.applied_at), "PP p")}`}
          {action.status === "rejected" && action.rejected_at && (
            <>
              Rejected {format(new Date(action.rejected_at), "PP p")}
              {action.rejection_reason ? ` — ${action.rejection_reason}` : null}
            </>
          )}
          {action.status === "superseded" && action.superseded_at && (
            <>
              Superseded {format(new Date(action.superseded_at), "PP p")}
              {action.superseded_reason
                ? ` — ${action.superseded_reason}`
                : null}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const AdSyncRunDetail: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const { showToast } = useToast();

  const [statusFilter, setStatusFilter] = useState<AdSyncActionStatus | "">(
    "proposed",
  );
  const [actionFilter, setActionFilter] = useState<AdSyncActionType | "">("");
  const filters: AdSyncProposalFilters = useMemo(
    () => ({
      status: statusFilter || undefined,
      action_type: actionFilter || undefined,
    }),
    [statusFilter, actionFilter],
  );

  const { data: run, isLoading: runLoading } = useAdSyncRun(runId);
  const { data: allRuns } = useAdSyncRuns();
  // mig 1750: only the newest computed run is approvable; an older one is read-only history
  const latestId = latestComputedRunId(allRuns ?? []);
  const newerRun =
    run && latestId && latestId !== run.id
      ? ((allRuns ?? []).find(
          (r) =>
            r.id === latestId &&
            new Date(r.started_at) > new Date(run.started_at),
        ) ?? null)
      : null;
  const {
    data: proposals,
    isLoading: propsLoading,
    error: propsError,
  } = useAdSyncProposals(runId, filters);

  const compute = useComputeAdSyncProposals();
  const apply = useApplyAdSyncAction(runId);
  const reject = useRejectAdSyncAction(runId);
  const batchApply = useApplyAdSyncActionsInChunks(runId);
  // mig 1278 (owner 2026-10-30): RM / TM scope is the AD hierarchy only -- no territory is picked or required here
  const { data: runProposed } = useAdSyncProposals(runId, RUN_PROPOSED_FILTER);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [batchResult, setBatchResult] = useState<ChunkedApplyResult | null>(
    null,
  );
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);

  // Everything "Select all" may pick: open, non-manual-review proposals of the
  // whole run (independent of the Status/Type filters above the list).
  const approvable = useMemo(
    () => (runProposed ?? []).filter(isBulkSelectable),
    [runProposed],
  );
  const approvableById = useMemo(
    () => new Map(approvable.map((a) => [a.id, a])),
    [approvable],
  );
  const approvableByType = useMemo(() => {
    const m = new Map<string, AdSyncAction[]>();
    approvable.forEach((a) => {
      const arr = m.get(a.action_type) ?? [];
      arr.push(a);
      m.set(a.action_type, arr);
    });
    return m;
  }, [approvable]);
  const manualReviewCount = useMemo(
    () =>
      (runProposed ?? []).filter(
        (a) => a.action_type === "manual_review_required",
      ).length,
    [runProposed],
  );
  const allActionsById = useMemo(() => {
    const m = new Map<string, AdSyncAction>();
    (runProposed ?? []).forEach((a) => m.set(a.id, a));
    (proposals ?? []).forEach((a) => m.set(a.id, a));
    return m;
  }, [runProposed, proposals]);
  // Only ids that are still open and approvable are ever sent (a row applied
  // or rejected elsewhere drops out of the selection after the next refetch).
  const selectedApprovable = useMemo(
    () => approvable.filter((a) => selected.has(a.id)),
    [approvable, selected],
  );

  const isBusy =
    apply.isPending ||
    reject.isPending ||
    batchApply.isPending ||
    compute.isPending;

  const grouped = useMemo(() => {
    const m = new Map<AdSyncActionType, AdSyncAction[]>();
    (proposals ?? []).forEach((p) => {
      const arr = m.get(p.action_type as AdSyncActionType) ?? [];
      arr.push(p);
      m.set(p.action_type as AdSyncActionType, arr);
    });
    return m;
  }, [proposals]);

  const handleSelectToggle = (id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleApply = async (action: AdSyncAction) => {
    try {
      const result = await apply.mutateAsync(action.id);
      if (result.status === "applied") {
        showToast("success", "Applied");
      } else if (result.status === "superseded") {
        showToast("info", `Superseded: ${result.reason ?? "stale"}`);
      }
    } catch (e: unknown) {
      const msg = readableApplyError(e, "Apply failed");
      if (isWaitingOnDependencyError(msg)) {
        showToast(
          "error",
          "Apply the dependent create action first, then retry.",
        );
      } else {
        showToast("error", msg);
      }
    }
  };

  const handleReject = async (action: AdSyncAction) => {
    const reason = window.prompt("Reason for rejection?", "Reviewed");
    if (!reason) {
      return;
    }
    try {
      await reject.mutateAsync({ actionId: action.id, reason });
      showToast("success", "Rejected");
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Reject failed";
      showToast("error", msg);
    }
  };

  const selectIds = (ids: string[]) => {
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  };
  const unselectIds = (ids: string[]) => {
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  };

  const runChunkedApply = async (actions: AdSyncAction[]) => {
    setBatchResult(null);
    setProgress({ done: 0, total: actions.length });
    try {
      const result = await batchApply.mutateAsync({
        actions,
        onProgress: (done, total) => setProgress({ done, total }),
      });
      setBatchResult(result);
      setSelected(new Set());
      const summary = `Applied ${result.applied} · Superseded ${result.superseded} · Failed ${result.failed}${
        result.not_attempted.length
          ? ` · Not attempted ${result.not_attempted.length}`
          : ""
      }`;
      if (result.failed > 0 || result.not_attempted.length > 0) {
        showToast("info", summary);
      } else {
        showToast("success", summary);
      }
    } catch (e: unknown) {
      const msg = readableApplyError(e, "Batch apply failed");
      showToast("error", msg);
    } finally {
      setProgress(null);
    }
  };

  const handleBatchApply = async () => {
    const toApply = selectedApprovable;
    if (toApply.length === 0) {
      return;
    }
    const perType = ACTION_ORDER.filter((t) =>
      toApply.some((a) => a.action_type === t),
    )
      .map(
        (t) =>
          `  ${ACTION_LABELS[t] ?? t}: ${toApply.filter((a) => a.action_type === t).length}`,
      )
      .join("\n");
    const ok = window.confirm(
      `Apply ${toApply.length} proposal${toApply.length === 1 ? "" : "s"} now?\n\n${perType}\n\n` +
        "Applying makes each change in the CRM immediately (there is no separate approval step). " +
        "Each item is still checked on its own; any that fail are listed afterwards.",
    );
    if (!ok) {
      return;
    }
    await runChunkedApply(toApply);
  };

  const handleCompute = async () => {
    if (!runId) {
      return;
    }
    try {
      const result = await compute.mutateAsync(runId);
      showToast(
        "success",
        `Computed ${result.proposed_total} proposals${
          result.partial_run
            ? ` (partial run: ${result.vacate_suppressed_reason ?? "unknown"})`
            : ""
        }`,
      );
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Compute failed";
      showToast("error", msg);
    }
  };

  if (runLoading || !run) {
    return (
      <div className="p-6 flex justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const approvableCount = approvable.length;
  const allSelected =
    approvableCount > 0 && selectedApprovable.length === approvableCount;

  const otherErrors = (batchResult?.results ?? []).filter(
    (r) => r.error && !isWaitingOnDependencyError(r.error),
  );
  const waitingErrors = (batchResult?.results ?? []).filter((r) =>
    isWaitingOnDependencyError(r.error),
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-4">
        <Link
          to="/admin/ad-sync"
          className="text-sm text-gray-500 hover:text-gray-900"
        >
          ← Back to runs
        </Link>
      </div>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Sync run · {format(new Date(run.started_at), "PP p")}
          </h1>
          <div className="text-sm text-gray-600 mt-1 flex flex-wrap gap-4">
            <span>
              Status: <strong>{run.status}</strong>
            </span>
            <span>Source: {adSyncSourceLabel(run.source)}</span>
            <span>Users: {run.user_count ?? "—"}</span>
            <span>
              Proposals computed:{" "}
              {run.proposals_computed_at
                ? format(new Date(run.proposals_computed_at), "PP p")
                : "—"}
            </span>
          </div>
        </div>
        {run.status === "complete" && !newerRun && (
          <Button
            variant="secondary"
            onClick={handleCompute}
            isLoading={compute.isPending}
          >
            {run.proposals_computed_at
              ? "Recompute proposals"
              : "Compute proposals"}
          </Button>
        )}
      </div>

      {newerRun && (
        <div className="mb-4 p-4 rounded border border-yellow-300 bg-yellow-50 text-sm text-yellow-900">
          A newer sync run ({format(new Date(newerRun.started_at), "PP p")},{" "}
          {adSyncSourceLabel(newerRun.source).toLowerCase()}) has computed
          proposals. Pending proposals from this run were marked superseded and
          reappear in the newer run.{" "}
          <Link
            to={`/admin/ad-sync/runs/${newerRun.id}`}
            className="font-medium underline"
          >
            Review the latest run →
          </Link>
        </div>
      )}

      {/* Filters */}
      <Card padding="md">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter((e.target.value as AdSyncActionStatus) || "")
              }
              className="text-sm rounded border-gray-300 focus:border-red-500 focus:ring-red-500"
            >
              <option value="">all</option>
              <option value="proposed">proposed</option>
              <option value="applied">applied</option>
              <option value="rejected">rejected</option>
              <option value="superseded">superseded</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600">Type:</label>
            <select
              value={actionFilter}
              onChange={(e) =>
                setActionFilter((e.target.value as AdSyncActionType) || "")
              }
              className="text-sm rounded border-gray-300 focus:border-red-500 focus:ring-red-500"
            >
              <option value="">all</option>
              {ACTION_ORDER.map((a) => (
                <option key={a} value={a}>
                  {ACTION_LABELS[a] ?? a}
                </option>
              ))}
            </select>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span
              className="text-xs text-gray-500"
              data-testid="ad-sync-selected-count"
            >
              {selectedApprovable.length} selected
            </span>
            <Button
              variant="primary"
              onClick={handleBatchApply}
              disabled={selectedApprovable.length === 0 || isBusy}
              isLoading={batchApply.isPending}
              data-testid="ad-sync-apply-selected"
            >
              {selectedApprovable.length > 0
                ? `Apply selected (${selectedApprovable.length})`
                : "Apply selected"}
            </Button>
          </div>
        </div>
        <div
          className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-3"
          data-testid="ad-sync-select-all-bar"
        >
          <Button
            variant="secondary"
            onClick={() => selectIds(approvable.map((a) => a.id))}
            disabled={approvableCount === 0 || allSelected || isBusy}
            data-testid="ad-sync-select-all"
          >
            {`Select all ${approvableCount} approvable proposal${
              approvableCount === 1 ? "" : "s"
            }`}
          </Button>
          <Button
            variant="ghost"
            onClick={() => setSelected(new Set())}
            disabled={selected.size === 0 || isBusy}
            data-testid="ad-sync-clear-selection"
          >
            Clear selection
          </Button>
          <span className="text-xs text-gray-600">
            Selects open proposals across the whole run, whatever the filters
            show. Nothing changes until you click{" "}
            <strong>Apply selected</strong> and confirm; applying makes the
            change at once.
            {manualReviewCount > 0 && (
              <>
                {" "}
                <span data-testid="ad-sync-manual-excluded">
                  The {manualReviewCount} manual-review item
                  {manualReviewCount === 1 ? " is" : "s are"} never included:
                  each needs its own decision.
                </span>
              </>
            )}
          </span>
        </div>
        {progress && (
          <div
            className="mt-3 text-sm text-gray-700"
            data-testid="ad-sync-apply-progress"
          >
            Applying… {progress.done} of {progress.total} done (in batches of
            100, creates first).
          </div>
        )}
      </Card>

      {/* Batch-apply result banner */}
      {batchResult && (
        <Card padding="md">
          <div className="text-sm space-y-2">
            <div>
              Batch result — applied <strong>{batchResult.applied}</strong>,
              superseded <strong>{batchResult.superseded}</strong>, failed{" "}
              <strong>{batchResult.failed}</strong>
              {batchResult.not_attempted.length > 0 && (
                <>
                  , not attempted{" "}
                  <strong>{batchResult.not_attempted.length}</strong>
                </>
              )}
              .
              {batchResult.superseded > 0 && (
                <span className="text-gray-600">
                  {" "}
                  Superseded means the CRM had already changed since the sync
                  ran, so the proposal was skipped, not applied.
                </span>
              )}
            </div>
            {batchResult.not_attempted.length > 0 && (
              <div
                className="p-3 bg-red-50 border border-red-200 rounded text-red-900"
                data-testid="ad-sync-not-attempted"
              >
                The apply stopped early:{" "}
                {readableApplyError(
                  { message: batchResult.stopped_error },
                  "a batch call failed",
                )}
                . <strong>{batchResult.not_attempted.length}</strong>{" "}
                proposal(s) were not sent and are still open. Everything listed
                as applied above did go through.
                <div className="mt-2">
                  <Button
                    variant="secondary"
                    disabled={isBusy}
                    onClick={() => {
                      const retry = batchResult.not_attempted
                        .map((id) => approvableById.get(id))
                        .filter((a): a is AdSyncAction => !!a);
                      void runChunkedApply(retry);
                    }}
                  >
                    Retry the ones not attempted
                  </Button>
                </div>
              </div>
            )}
            {waitingErrors.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded text-amber-900">
                <strong>{waitingErrors.length}</strong> action(s) hit
                WAITING_ON_DEPENDENCY — apply the dependent create action first,
                then retry the failed actions.
                <div className="mt-2">
                  <Button
                    variant="secondary"
                    disabled={isBusy}
                    onClick={() => {
                      const retry = waitingErrors
                        .map((r) => approvableById.get(r.id))
                        .filter((a): a is AdSyncAction => !!a);
                      void runChunkedApply(retry);
                    }}
                  >
                    Retry failed dependencies
                  </Button>
                </div>
              </div>
            )}
            {otherErrors.length > 0 && (
              <ul
                className="p-3 bg-red-50 border border-red-200 rounded text-red-900 space-y-1"
                data-testid="ad-sync-batch-errors"
              >
                <li className="font-medium">
                  {otherErrors.length} proposal(s) failed and are still open:
                </li>
                {otherErrors.map((r) => {
                  const a = allActionsById.get(r.id);
                  return (
                    <li key={r.id}>
                      {a ? (
                        <>
                          {ACTION_LABELS[a.action_type] ?? a.action_type}
                          {actionDisplayName(a)
                            ? ` · ${actionDisplayName(a)}`
                            : ""}{" "}
                        </>
                      ) : null}
                      <span className="font-mono text-xs">
                        {r.id.slice(0, 8)}
                      </span>{" "}
                      — {readableApplyError({ message: r.error }, "failed")}
                    </li>
                  );
                })}
              </ul>
            )}
            <button
              onClick={() => setBatchResult(null)}
              className="text-xs text-gray-500 hover:text-gray-900"
            >
              Dismiss
            </button>
          </div>
        </Card>
      )}

      {/* Proposals grouped by action_type */}
      {propsLoading && (
        <div className="p-12 flex justify-center">
          <LoadingSpinner />
        </div>
      )}
      {propsError && (
        <Card padding="md">
          <div className="text-sm text-red-800">
            Failed to load proposals: {String(propsError)}
          </div>
        </Card>
      )}
      {!propsLoading && !propsError && (proposals?.length ?? 0) === 0 && (
        <Card padding="md">
          <div className="p-6 text-center text-gray-500 text-sm">
            No proposals match the current filters.
            {!run.proposals_computed_at && (
              <div className="mt-2">
                Run hasn't been computed yet — click{" "}
                <strong>Compute proposals</strong> above.
              </div>
            )}
          </div>
        </Card>
      )}

      {!propsLoading && (proposals?.length ?? 0) > 0 && (
        <div className="space-y-6 mt-4">
          {ACTION_ORDER.filter((t) => grouped.has(t)).map((t) => {
            const list = grouped.get(t) ?? [];
            const groupApprovable = approvableByType.get(t) ?? [];
            const groupSelected = groupApprovable.filter((a) =>
              selected.has(a.id),
            ).length;
            return (
              <section key={t} data-testid={`ad-sync-group-${t}`}>
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    {ACTION_LABELS[t]} ({list.length})
                  </h2>
                  {t === "manual_review_required" && (
                    <span className="text-xs text-gray-500">
                      Not bulk-selectable: decide each one individually.
                    </span>
                  )}
                  {t !== "manual_review_required" &&
                    groupApprovable.length > 0 && (
                      <>
                        <button
                          type="button"
                          className="text-xs text-red-700 hover:text-red-900 underline disabled:text-gray-400 disabled:no-underline"
                          onClick={() =>
                            selectIds(groupApprovable.map((a) => a.id))
                          }
                          disabled={
                            isBusy || groupSelected === groupApprovable.length
                          }
                          data-testid={`ad-sync-select-group-${t}`}
                        >
                          Select all {groupApprovable.length} in this group
                        </button>
                        {groupSelected > 0 && (
                          <button
                            type="button"
                            className="text-xs text-gray-600 hover:text-gray-900 underline disabled:text-gray-400"
                            onClick={() =>
                              unselectIds(groupApprovable.map((a) => a.id))
                            }
                            disabled={isBusy}
                            data-testid={`ad-sync-clear-group-${t}`}
                          >
                            Clear this group ({groupSelected} selected)
                          </button>
                        )}
                      </>
                    )}
                </div>
                <div className="space-y-3">
                  {list.map((a) => (
                    <ProposalCard
                      key={a.id}
                      action={a}
                      selected={selected.has(a.id)}
                      onSelectToggle={handleSelectToggle}
                      onApply={handleApply}
                      onReject={handleReject}
                      busy={isBusy}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdSyncRunDetail;
